<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from "vue";

const props = defineProps({
  open: { type: Boolean, default: false },
  // Accessible name for the dialog (falls back to a generic label).
  label: { type: String, default: "Dialog" },
});
const emit = defineEmits(["close"]);

const mounted = ref(props.open);
const shown = ref(false);
const sheetEl = ref(null);
let timer = null;
let lastFocused = null;

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

function lockScroll(lock) {
  // Prevent the page behind the sheet from scrolling while it's open.
  document.documentElement.style.overflow = lock ? "hidden" : "";
}

function onKeydown(e) {
  if (e.key === "Escape") {
    e.stopPropagation();
    emit("close");
    return;
  }
  if (e.key !== "Tab" || !sheetEl.value) return;
  // Simple focus trap: keep Tab cycles inside the sheet.
  const nodes = [...sheetEl.value.querySelectorAll(FOCUSABLE)].filter(
    (n) => n.offsetParent !== null,
  );
  if (!nodes.length) return;
  const first = nodes[0];
  const last = nodes[nodes.length - 1];
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

watch(
  () => props.open,
  (open) => {
    clearTimeout(timer);
    if (open) {
      lastFocused = document.activeElement;
      mounted.value = true;
      lockScroll(true);
      document.addEventListener("keydown", onKeydown, true);
      timer = setTimeout(() => {
        shown.value = true;
        nextTick(() => sheetEl.value?.focus());
      }, 20);
    } else {
      shown.value = false;
      document.removeEventListener("keydown", onKeydown, true);
      lockScroll(false);
      // Return focus to whatever opened the sheet.
      if (lastFocused && typeof lastFocused.focus === "function") {
        lastFocused.focus();
        lastFocused = null;
      }
      timer = setTimeout(() => (mounted.value = false), 320);
    }
  },
);

onBeforeUnmount(() => {
  clearTimeout(timer);
  document.removeEventListener("keydown", onKeydown, true);
  lockScroll(false);
});
</script>

<template>
  <template v-if="mounted">
    <div
      class="btm-sheet-scrim"
      :class="{ show: shown }"
      @click="emit('close')"
    />
    <div
      ref="sheetEl"
      class="btm-sheet"
      :class="{ show: shown }"
      role="dialog"
      aria-modal="true"
      :aria-label="label"
      tabindex="-1"
    >
      <div class="btm-sheet-head">
        <button
          type="button"
          class="btm-sheet-grab"
          aria-label="Dismiss"
          @click="emit('close')"
        >
          <span />
        </button>
        <button
          type="button"
          class="btm-sheet-close"
          aria-label="Close"
          @click="emit('close')"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>
      <div class="btm-sheet-body"><slot /></div>
    </div>
  </template>
</template>
